<!--
  ~ Copyright (C) 2026 - Potentially Ltd
  ~
  ~ Please see distribution for license.
-->

<ptl-v3-dialog-shell
  [title]="'translations.adminEnrolment.person.title' | transloco"
  [description]="data.contentTitle"
  [closeDisabled]="enrolling()"
  (closed)="close()"
>
  <div class="enrol-dialog">
    <div class="enrol-dialog__field">
      <span class="enrol-dialog__label">{{ 'translations.adminEnrolment.person.find' | transloco }}</span>
      @if (selected(); as user) {
        <div class="enrol-dialog__selected">
          <span class="enrol-dialog__selected-info">
            <span class="enrol-dialog__selected-name">{{ displayName() }}</span>
            <span class="enrol-dialog__muted">{{ user.email }}</span>
          </span>
          <button
            type="button"
            class="enrol-dialog__icon-button"
            [attr.aria-label]="'translations.global.ariaLabels.remove' | transloco: { name: displayName() }"
            (click)="clearSelected()"
          >
            <ptl-icon [icon]="LucideX" size="s"></ptl-icon>
          </button>
        </div>
      } @else {
        <ptl-v3-member-search
          [placeholder]="'translations.adminEnrolment.person.findPlaceholder' | transloco"
          (memberSelected)="onSelected($event)"
        ></ptl-v3-member-search>
      }
    </div>

    @if (!selected()) {
      <div class="enrol-dialog__divider">{{ 'translations.adminEnrolment.person.orNew' | transloco }}</div>

      <div class="enrol-dialog__grid">
        <div class="enrol-dialog__field enrol-dialog__field--wide">
          <ptl-sg-input
            class="f_enrol-email"
            type="email"
            [label]="'translations.adminEnrolment.person.email' | transloco"
            [value]="email()"
            [invalid]="emailInvalid()"
            (inputChange)="email.set($event)"
          ></ptl-sg-input>
          @if (emailInvalid()) {
            <p class="enrol-dialog__error">{{ 'translations.adminEnrolment.problem.invalidEmail' | transloco }}</p>
          } @else if (needsAccount()) {
            <p class="enrol-dialog__hint">
              <ptl-icon [icon]="LucideInfo" size="xs" aria-hidden="true"></ptl-icon>
              {{ 'translations.adminEnrolment.person.newAccount' | transloco }}
            </p>
          }
        </div>
        <ptl-sg-input
          [label]="'translations.adminEnrolment.person.firstName' | transloco"
          [description]="needsAccount() ? ('translations.adminEnrolment.person.required' | transloco) : ''"
          [value]="firstName()"
          (inputChange)="firstName.set($event)"
        ></ptl-sg-input>
        <ptl-sg-input
          [label]="'translations.adminEnrolment.person.lastName' | transloco"
          [description]="needsAccount() ? ('translations.adminEnrolment.person.required' | transloco) : ''"
          [value]="lastName()"
          (inputChange)="lastName.set($event)"
        ></ptl-sg-input>
        <div class="enrol-dialog__field enrol-dialog__field--wide">
          <ptl-sg-input
            [label]="'translations.adminEnrolment.person.externalId' | transloco"
            [description]="'translations.adminEnrolment.person.externalIdHint' | transloco"
            [value]="externalId()"
            (inputChange)="externalId.set($event)"
          ></ptl-sg-input>
        </div>
      </div>
    }

    @if (blockingReasonKey(); as reasonKey) {
      <p class="enrol-dialog__notice" role="status">{{ reasonKey | transloco }}</p>
    }

    @if (seatsOver() || outsideAudience()) {
      <div class="enrol-dialog__warning" role="status">
        <p class="enrol-dialog__warning-title">
          <ptl-icon [icon]="LucideTriangleAlert" size="xs" aria-hidden="true"></ptl-icon>
          {{ 'translations.adminEnrolment.warning.title' | transloco }}
        </p>
        <ul class="enrol-dialog__warning-list">
          @if (seatsOver(); as seats) {
            <li>
              {{
                'translations.adminEnrolment.warning.seatsPerson'
                  | transloco: { limit: seats.limit, taken: seats.taken, name: displayName() }
              }}
            </li>
          }
          @if (outsideAudience()) {
            <li>{{ 'translations.adminEnrolment.warning.outsideAudiencePerson' | transloco: { name: displayName() } }}</li>
          }
        </ul>
      </div>
    }

    <div class="enrol-dialog__notify">
      <ptl-sg-checkbox [checked]="notify()" (checkboxChange)="notify.set($event)">
        <span class="enrol-dialog__notify-text">
          <span class="enrol-dialog__notify-title">
            {{
              'translations.adminEnrolment.person.notify'
                | transloco: { name: displayName() || ('translations.adminEnrolment.person.them' | transloco) }
            }}
          </span>
          <span class="enrol-dialog__muted">
            {{ 'translations.adminEnrolment.notify.help' | transloco: { title: data.contentTitle } }}
            @if (!data.published) {
              {{ 'translations.adminEnrolment.notify.unpublished' | transloco }}
            }
          </span>
        </span>
      </ptl-sg-checkbox>
    </div>

    @if (failure(); as message) {
      <p class="enrol-dialog__error" role="alert">{{ message }}</p>
    }
  </div>

  <ng-container dialogFooter>
    <ptl-button
      type="secondary-neutral"
      size="m"
      [text]="'translations.global.button.cancel' | transloco"
      [disabled]="enrolling()"
      (clicked)="close()"
    ></ptl-button>
    <ptl-button
      type="primary"
      size="m"
      class="f_enrol-person"
      [loading]="enrolling()"
      [disabled]="!canEnrol()"
      [text]="
        displayName()
          ? ('translations.adminEnrolment.person.enrolName' | transloco: { name: displayName() })
          : ('translations.adminEnrolment.person.enrol' | transloco)
      "
      (clicked)="enrol()"
    ></ptl-button>
  </ng-container>
</ptl-v3-dialog-shell>
